<template>
  <ul class="color-pick-table">
    <li
      v-for="(color, index) in localColors"
      :key="index"
      class="color-pick-table__color-cell"
      @click="handleColorClick(color, $event)"
    >
      <div
        :class="`color-pick-table__color-cell-inner
                    ${
                      showColorPicker &&
                      index === localColors.length - 1 &&
                      'color-pick-table__color-cell-inner--color-picker'
                    }

                    ${
                      calculateColorBrightness(convertRGBtoHEX(color)) > 170 &&
                      'color-pick-table__color-cell-inner--dark'
                    }

                    ${checkMode && checkedColor === color && 'color-pick-table__color-cell-inner--checked'}

                    ${
                      showColorPicker &&
                      index === localColors.length - 1 &&
                      checkedColor === 'custom-color' &&
                      'color-pick-table__color-cell-inner--checked color-pick-table__color-cell-inner--dark'
                    }`"
        :style="{
          ...swatchStyle(color, 88),
          borderRadius: defineBorderRadius(index, localColors.length),
        }"
        v-html="checkMode ? icons.interfaceElements.icoSelected : icons.interfaceElements.plusIcon"
      />
      <div
        v-if="paletteName(color)"
        :class="`color-pick-table__palette-label
                    ${
                      calculateColorBrightness(convertRGBtoHEX(color)) > 180 && 'color-pick-table__palette-label--dark'
                    }`"
      >
        {{ paletteName(color) }}
      </div>
    </li>
    <fs-popper-pr-edit ref="popperColorPickerSingle">
      <color-picker-single :save-func="pickColor" />
    </fs-popper-pr-edit>
  </ul>
</template>

<script>
import { calculateColorBrightness } from '@lib/colorManagement';
import { convertRGBtoHEX } from '@lib/colorManagement';
import { swatchStyle } from '@lib/color-swatch';
import cloneDeep from 'lodash/cloneDeep';
import { draftAsColor, draftFromValues } from '@lib/color-picker-draft';

// Components
import FsPopperPrEdit from '@components/poppers/fs-popper-pr-edit.vue';
import ColorPickerSingle from './single.vue';

// Icons
import interfaceElementsIcons from '@lib/icons-imports/interface-elements';

export default {
  props: {
    colors: {
      type: Array,
      default: null, // Defaults to $store.colors.baseColors in computed (can't access this/$store in props default functions)
    },
    borderRadius: {
      type: Number,
      default: 5,
    },
    checkMode: Boolean,
    showColorPicker: {
      type: Boolean,
      default: false,
    },
  },
  components: { FsPopperPrEdit, ColorPickerSingle },
  data() {
    return {
      checkedColor: undefined,
      icons: {
        interfaceElements: interfaceElementsIcons,
      },
    };
  },
  computed: {
    localColors() {
      const colors = this.colors ? [...this.colors] : [...this.$store.colors.baseColors];

      if (this.showColorPicker) {
        colors.push({ name: 'color-picker', r: 0, g: 0, b: 0, paletteIds: [] });
      }

      return colors;
    },
  },
  methods: {
    calculateColorBrightness,
    convertRGBtoHEX,
    swatchStyle,
    paletteName(color) {
      const paletteId = color.paletteIds?.[0];
      return paletteId && this.$store.colors.palettes.find((p) => p.id === paletteId)?.name;
    },
    // Emitted colours are always flat, and carry the picker state when they came from a
    // draft rather than a saved colour — that is what tells a consumer to save a new one.
    pickColor(_args, pickerState) {
      this.checkedColor = 'custom-color';
      this.$emit('color-pick', { ...draftAsColor(pickerState), pickerState });
    },
    handleColorClick(color, event) {
      if (color.name === 'color-picker') {
        this.$refs.popperColorPickerSingle.toggle(event);
        return;
      }

      if (this.checkMode) {
        if (this.checkedColor === color) {
          this.checkedColor = undefined;
          color = undefined;
        } else {
          this.checkedColor = color;
        }
      }

      this.$emit(
        'color-pick',
        color?.id != null ? cloneDeep(color) : color && { ...cloneDeep(color), pickerState: draftFromValues(color) }
      );
    },
    defineBorderRadius(index, colorsListLength) {
      const isCellFirst = index === 0;
      const isCellLastInTopRow = index === 4;
      const isCellFirstInBottomRow =
        index === colorsListLength - (colorsListLength % 5 === 0 ? 5 : colorsListLength % 5);
      const isCellLast = index === colorsListLength - 1;
      const isCellLastOnPenultimateRow = index + 1 === colorsListLength - (colorsListLength % 5);

      const $ = this.borderRadius;
      let radiusArray = [];

      if (colorsListLength <= 5) {
        if (isCellFirst) radiusArray = [$, 0, 0, $];
        if (isCellLast) radiusArray = [0, $, $, 0];
        if (isCellFirst && isCellLast) radiusArray = [$, $, $, $];
      }

      if (colorsListLength > 5 && colorsListLength % 5 === 0) {
        if (isCellFirst) radiusArray = [$, 0, 0, 0];
        if (isCellLast) radiusArray = [0, 0, $, 0];
        if (isCellLastInTopRow) radiusArray = [0, $, 0, 0];
        if (isCellFirstInBottomRow) radiusArray = [0, 0, 0, $];
      }

      if (colorsListLength > 5 && colorsListLength % 5 === 1) {
        if (isCellFirst) radiusArray = [$, 0, 0, 0];
        if (isCellLastInTopRow) radiusArray = [0, $, 0, 0];
        if (isCellLast) radiusArray = [0, 0, $, $];
        if (isCellLastOnPenultimateRow) radiusArray = [0, 0, $, 0];
        if (isCellLastInTopRow && isCellLastOnPenultimateRow) radiusArray = [0, $, $, 0];
      }

      if (colorsListLength > 5 && colorsListLength % 5 > 1) {
        if (isCellFirst) radiusArray = [$, 0, 0, 0];
        if (isCellLastInTopRow) radiusArray = [0, $, 0, 0];
        if (isCellFirstInBottomRow) radiusArray = [0, 0, 0, $];
        if (isCellLast) radiusArray = [0, 0, $, 0];
        if (isCellLastOnPenultimateRow) radiusArray = [0, 0, $, 0];
        if (isCellLastInTopRow && isCellLastOnPenultimateRow) radiusArray = [0, $, $, 0];
      }

      return radiusArray.join('px ') + 'px';
    },
  },
};
</script>

<style scoped lang="scss">
@import '@styles/variables';

.color-pick-table {
  width: 100%;
  padding: 0;
  margin: 0;
  display: flex;
  flex-wrap: wrap;
  border-radius: 0.5rem;
  list-style: none;

  &__color-cell {
    width: calc(100% / 5);
    padding: 0;
    margin: 0;
    position: relative;
    user-select: none;

    &--color-picker {
      background: #f00;
    }

    &:hover {
      .color-pick-table__palette-label {
        top: 0.2rem;
        left: 0.5rem;
        transform: scale(1.13);
      }
    }
  }

  &__color-cell-color-picker-image {
    width: 100%;
    height: 100%;
    position: absolute;
    top: 0;
    left: 0;
    z-index: 4;
  }

  &__color-cell-inner {
    width: 100%;
    padding-top: 100%;
    position: relative;
    z-index: 0;
    transform: scale(1);
    font-size: 0;
    line-height: 0;
    cursor: pointer;
    box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.1);
    transition:
      transform 0.1s ease,
      box-shadow 0.1s ease;

    &::before {
      content: '';
      width: 2.4rem;
      height: 2.4rem;
      padding-bottom: 1px;
      box-sizing: border-box;
      display: flex;
      justify-content: center;
      align-items: center;
      align-content: center;
      border: 1px #fff solid;
      border-radius: 10rem;
      position: absolute;
      top: calc(50% - 1.2rem);
      left: calc(50% - 1.2rem);
      font-size: 2.1rem;
      color: #fff;
      background: $c-dark;
      opacity: 0;
      transition: opacity 0.07s ease;
    }

    :deep(svg) {
      width: 1rem;
      height: 1rem;
      position: absolute;
      top: calc(50% - 0.5rem);
      left: calc(50% - 0.5rem);
      z-index: 2;
      transform: scale(1);
      opacity: 0;
      fill: #fff;
      transition:
        transform 0.07s ease,
        opacity 0.07s ease;
    }

    &:hover {
      transform: scale(1.13);
      z-index: 1;
      box-shadow: 0 0 1rem rgba(0, 0, 0, 0.2);

      &::before,
      :deep(svg) {
        opacity: 1;
      }
    }

    &--color-picker {
      background: 50% 50%/190% no-repeat url(@assets/images/color-picker-palette-bg.jpg);

      &:hover {
        &::before {
          display: none;
        }

        :deep(svg) {
          display: none;
        }
      }
    }

    &--checked {
      transform: scale(1) !important;
      box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.1) !important;

      &::before {
        opacity: 0 !important;
      }

      :deep(svg) {
        opacity: 1 !important;
        transform: scale(3.5);
      }
    }

    &--dark.color-pick-table__color-cell-inner--checked {
      :deep(svg) {
        fill: rgba(73, 73, 73, 1) !important;
      }
    }
  }

  &__palette-label {
    position: absolute;
    top: 0.6rem;
    left: 0.6rem;
    z-index: 1;
    font-size: 0.8rem;
    line-height: 0.8rem;
    text-transform: uppercase;
    color: rgba(255, 255, 255, 0.8);
    transform: scale(1);
    transition:
      transform 0.1s ease,
      top 0.1s ease,
      left 0.1s ease;
    pointer-events: none;

    &--dark {
      color: $c-dark;
    }
  }
}
</style>
